import {
    goToImageByRoadData,
    selectHoveredRoadData,
    noMoreHoveredRoadData,
    unselectRoadDataAndSave,
    selectAnnotationInEditionAndJump,
    selectSelectedRoadData,
} from '../../../../../../core/legacy/actions';
import { FC, useCallback } from 'react';
import {
    selectedRoadDataIdSelector,
    hoveredRoadDataIdSelector,
} from '../../../../../../core/legacy/selectors/streetView';
import AttributeTypeEnum from '../../../../../../core/legacy/utils/AttributeTypeEnum';
import { findAttributeValuesByType } from '../../../../../../core/legacy/utils/roadDataUtils';
import { ElementData as AnnotationData } from '../../../ui/panels/right-panel/ElementSummary/styles';
import LandmarkValue from '../../../../../components/streetView/roadDatasPanel/values/LandmarkValue';
import useScroll from './use-scroll';
import { useAppDispatch, useAppSelector } from '../../../../../../types';
import type { RoadData } from '../../../../../../core/domain/Roaddata';
import { Box, SxProps, Typography } from '@mui/material';
import { resolveRoadDataName } from '../../../../../../utils/resolve-road-data-name';
import { resolveRoadDataColor } from '../../../../../../utils/resolve-road-data-color';
import { SmallChip } from '../../../../../components/streetView/roadDatasPanel/common';
import { theme } from '../../../../style/theme';
import { translateTags } from '../../../../../../core/legacy/utils/translations';
import Block from '../../../../../../design/panel/Block';
import Row from '../../../../../../design/Row';

const detailsSx: SxProps = {
    display: 'flex',
    gap: 2,
    justifyContent: 'end',
};

const mainSx = (roadDataColor?: string): SxProps => ({
    alignItems: 'flex-start',
    borderLeft: 3.5,
    borderColor: roadDataColor,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 'bold',
    justifyContent: 'center',
    pl: 2,
});

const titleSx: SxProps = {
    color: theme.color.black.main,
    fontSize: theme.font.size.small,
    fontWeight: 'bold',
};

type Props = {
    roadData: RoadData;
    summaryOffsetHeight: number;
};

const RoadDataSummary: FC<Props> = ({ roadData, summaryOffsetHeight }) => {
    const dispatch = useAppDispatch();
    const roadDataName = resolveRoadDataName(roadData);
    const roadDataColor = resolveRoadDataColor(roadData);
    const selectedRoadDataId = useAppSelector(selectedRoadDataIdSelector);
    const hoveredRoadDataId = useAppSelector(hoveredRoadDataIdSelector);
    const { id, landmarkStart, tags } = roadData;
    const isImageType = roadData.type === 'IMAGE';

    const handleSelect = useCallback(async () => {
        if (isImageType) {
            dispatch(selectSelectedRoadData(id));
        } else {
            dispatch(goToImageByRoadData(id));
        }
    }, [id, isImageType]);
    const handleUnselect = useCallback(async () => dispatch(unselectRoadDataAndSave(id)), [id]);

    const handleMouseOver = (): void => {
        dispatch(selectHoveredRoadData(id));
    };
    const handleMouseLeave = (): void => {
        dispatch(noMoreHoveredRoadData());
    };
    const handleDoubleClick = (): void => {
        if (!isImageType) {
            dispatch(selectAnnotationInEditionAndJump(id));
        }
    };

    const cumulStartAttr = isImageType ? null : findAttributeValuesByType(roadData, AttributeTypeEnum.CUMUL_START);
    const roadDataCumulStart = cumulStartAttr?.value?.numeric ?? '0';
    const unit = cumulStartAttr?.attribute?.unit ?? 'm';
    const selected = selectedRoadDataId === id;
    const hovered = hoveredRoadDataId === id && !selected;
    const rubricElementRef = useScroll(id, summaryOffsetHeight);

    return (
        <Block
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            selected={selected}
            hovered={hovered}
            onSelect={handleSelect}
            onUnselect={handleUnselect}
            onDoubleClick={handleDoubleClick}
            ref={rubricElementRef}
        >
            <Row>
                <Box sx={mainSx(roadDataColor)}>
                    <Typography sx={titleSx}>{roadDataName}</Typography>
                    {tags?.length ? <SmallChip label={translateTags('taggedRoadData')} /> : null}
                </Box>
                <Box sx={detailsSx}>
                    <AnnotationData>{`${+roadDataCumulStart}${unit}`}</AnnotationData>
                    <AnnotationData>
                        {roadData.landmarkStart && <LandmarkValue landmark={landmarkStart} />}
                    </AnnotationData>
                </Box>
            </Row>
        </Block>
    );
};

export default RoadDataSummary;
